import type {TemplateNode,NativeTemplate} from '@/uptk/core/components/moduleEntity/template/templateTypes';
import type {CalculatorConfig} from '@/uptk/core/components/moduleEntity/calc/calcModel';
import type {ScreenBindingProposal} from './templateScreenBindings';

export type CalculatorTemplatePath=(string|number)[];
export type CalculatorTemplatePlan={
 fixed?:CalculatorTemplatePath[];
 inputs:{path:CalculatorTemplatePath;field:string;label?:string}[];
 slots:{path:CalculatorTemplatePath;ref:'CalculatorValue'|'CalculatorChart'|'CalculatorRows'|'CalculatorCsv'|'CalculatorText';props:Record<string,unknown>}[];
};
/** Uses the ordinary bulk-binding transaction and its STUDIO-REPLAY journal. */
export function calculatorTemplateProposal(template:NativeTemplate,calculator:CalculatorConfig,plan:CalculatorTemplatePlan,path:CalculatorTemplatePath,messages:Record<string,Record<string,string>>):ScreenBindingProposal{
 return {path,node:bindCalculatorTemplate(template,calculator,plan,messages),messages,sample:calculator.version,context:calculator.version,reason:'aggregate',confidence:1};
}
/** A reviewed binding plan changes behavior only; every container and style comes from the compiled capture. */
export function bindCalculatorTemplate(template:NativeTemplate,config:CalculatorConfig,plan:CalculatorTemplatePlan,messages:Record<string,Record<string,string>>={}):TemplateNode{
 if(plan.inputs.length>16||plan.slots.length>100)throw Error('calculator.template.bounds');
 const root=structuredClone(template.root);
 const at=(path:CalculatorTemplatePath):TemplateNode=>{
  let value:unknown=root;
  for(const key of path){if(!value||typeof value!=='object')throw Error('calculator.template.path');value=(value as Record<string|number,unknown>)[key];}
  if(!value||typeof value!=='object'||!('type' in value))throw Error('calculator.template.path');
  return value as TemplateNode;
 };
 if(root.type!=='el')throw Error('calculator.template.root');
 root.state={...root.state,...Object.fromEntries(config.inputs.map(input=>[input.field,input.default]))};
 const claimed=new Set<string>();
 for(const input of plan.inputs){
  const node=at(input.path),field=config.inputs.find(field=>field.field===input.field);
  if(node.type!=='el'||!['input','select'].includes(node.tag)||!field||claimed.has(input.field))throw Error('calculator.template.input');
  claimed.add(input.field);node.input={state:field.field,...(field.control==='select'?{}:{min:field.min,max:field.max,step:field.step})};delete node.candidate;
  if(input.label)node.attrs={...node.attrs,'aria-label':input.label};
  if(node.tag==='select'){
   const options=node.children.filter((child):child is Extract<TemplateNode,{type:'el'}>=>child.type==='el'&&child.tag==='option');
   if(options.length!==field.options?.length)throw Error('calculator.template.options');
   options.forEach((option,index)=>{option.attrs={...option.attrs,value:field.options![index].key};});
  }
 }
 if(claimed.size!==config.inputs.length)throw Error('calculator.template.inputs');
 const activate=(node:TemplateNode)=>{
  if(node.type!=='el')return;
  if(node.tag==='button'&&node.attrs?.type==='submit'){
   const field=config.inputs[0].field;
   node.attrs={...node.attrs,type:'button'};node.on={click:{update:{state:{[field]:{state:field}}}}};delete node.candidate;
  }
  node.children.forEach(activate);
 };activate(root);
 for(const path of plan.fixed??[]){const node=at(path);if(node.type!=='text')throw Error('calculator.template.literal');delete node.candidate;}
 for(const slot of plan.slots){
  const target=at(slot.path);
  if(slot.ref==='CalculatorRows'&&target.type==='el'&&target.tag==='tbody'){target.children=[{type:'calculator-slot',slot:slot.ref,props:structuredClone(slot.props)}];continue;}
  if(!slot.path.length)throw Error('calculator.template.slot');
  const parent=at(slot.path.slice(0,-2));
  if(parent.type!=='el'||slot.path[slot.path.length-2]!=='children'||typeof slot.path[slot.path.length-1]!=='number')throw Error('calculator.template.slot');
  const text=target.type==='text'?(target.token??messages[target.i18n??'']?.fr??''):'';
  const spacing=slot.ref==='CalculatorValue'?{prefix:text.match(/^\s*/)?.[0]??'',suffix:text.match(/\s*$/)?.[0]??''}:{};
  parent.children[Number(slot.path[slot.path.length-1])]={type:'calculator-slot',slot:slot.ref,props:{...structuredClone(slot.props),...spacing},anchor:structuredClone(target)};
 }
 const calculator=structuredClone(config);delete calculator.history;delete calculator.saveDefaults;
 return {type:'calculator-template',calculator,root};
}
